import { useEffect, useMemo, useState } from "react";
import { Public } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarView } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { Compass, GoogleLogo, MicrosoftOutlookLogo, AppleLogo, CheckCircle, Clock, MapPin, Globe } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const COMMON_TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
  "UTC",
];

function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function tzLabel(tz) {
  try {
    const offset = dayjs().tz(tz).format("Z");
    return `${tz.replace(/_/g, " ")} (UTC${offset})`;
  } catch {
    return tz;
  }
}

export default function PublicBooking() {
  const [info, setInfo] = useState(null);
  const [date, setDate] = useState(new Date());
  const [slot, setSlot] = useState(null);
  const [tz, setTz] = useState(detectTimezone());
  const [form, setForm] = useState({ prospect_name: "", prospect_email: "", notes: "" });
  const [booked, setBooked] = useState(null);

  useEffect(() => { Public.info().then(setInfo); }, []);

  // Ensure the detected TZ is in the option list (some machines report obscure IANA names)
  const timezoneOptions = COMMON_TIMEZONES.includes(tz) ? COMMON_TIMEZONES : [tz, ...COMMON_TIMEZONES];

  // Compute available slots for the selected date, in the booker's TZ.
  // Slots are generated in the workspace's TZ (owner's work hours), then converted for display.
  const slots = useMemo(() => {
    if (!info) return [];
    const wsTz = info.workspace_tz || "UTC";
    const startHour = info.work_start_hour ?? 9;
    const endHour = info.work_end_hour ?? 17;
    const slotMin = info.slot_minutes || 30;
    const workDays = info.work_days || [0, 1, 2, 3, 4];
    const busy = (info.busy || []).map(b => ({
      s: dayjs(b.starts_at).valueOf(),
      e: dayjs(b.ends_at).valueOf(),
    }));

    // Work in the booker's selected local date (year-month-day), then interpret each candidate
    // slot as a wall-clock time in the workspace TZ. We build one candidate every `slotMin`
    // minutes starting at `startHour` in the workspace TZ.
    // Range: we scan the workspace day corresponding to the picked date so that DST etc. is
    // handled naturally.
    const pickedIso = dayjs(date).format("YYYY-MM-DD");
    // Owner's day-of-week for the picked date, at 12:00 workspace TZ (safe from DST edges)
    const ownerDay = dayjs.tz(`${pickedIso} 12:00`, "YYYY-MM-DD HH:mm", wsTz).day(); // 0=Sun..6=Sat
    // Map dayjs (Sun=0..Sat=6) → our convention (Mon=0..Sun=6)
    const wsDayIdx = (ownerDay + 6) % 7;
    if (!workDays.includes(wsDayIdx)) return [];

    const out = [];
    const now = dayjs();
    for (let h = startHour; h < endHour; h++) {
      for (let m = 0; m < 60; m += slotMin) {
        const wsSlot = dayjs.tz(`${pickedIso} ${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`, "YYYY-MM-DD HH:mm", wsTz);
        const startMs = wsSlot.valueOf();
        const endMs = wsSlot.add(slotMin, "minute").valueOf();
        if (startMs < now.valueOf()) continue;
        const overlapsBusy = busy.some(b => startMs < b.e && endMs > b.s);
        if (overlapsBusy) continue;
        // Display label in booker's TZ
        const label = wsSlot.tz(tz).format("HH:mm");
        out.push({ label, iso: wsSlot.toISOString(), endIso: wsSlot.add(slotMin, "minute").toISOString() });
      }
    }
    return out;
  }, [info, date, tz]);

  // If the selected slot is no longer available on the new date/tz, clear it.
  useEffect(() => {
    if (slot && !slots.find(s => s.iso === slot.iso)) setSlot(null);
  }, [slots, slot]);

  const submit = async () => {
    if (!slot || !form.prospect_name || !form.prospect_email) return toast.error("Fill all fields");
    try {
      const res = await Public.book({
        prospect_name: form.prospect_name,
        prospect_email: form.prospect_email,
        starts_at: slot.iso,
        ends_at: slot.endIso,
        notes: form.notes,
        timezone: tz,
      });
      setBooked(res);
    } catch { toast.error("Booking failed"); }
  };

  if (booked) {
    const ics = `${API}/appointments/${booked.id}/ics`;
    const bookedTz = booked.timezone || tz;
    const whenStr = dayjs(booked.starts_at).tz(bookedTz).format("dddd, MMMM D, YYYY [at] HH:mm");
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center p-6">
        <Card className="max-w-lg w-full p-10 rounded-md border-neutral-200 bg-white" data-testid="booking-confirmed">
          <div className="flex items-center gap-3">
            <CheckCircle size={40} weight="fill" className="text-emerald-600" />
            <div>
              <div className="overline">Confirmed</div>
              <div className="font-display text-3xl mt-1">You're booked</div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-neutral-200 space-y-2 text-sm">
            <div><span className="overline mr-2">Meeting</span>{booked.title}</div>
            <div data-testid="booking-when"><span className="overline mr-2">When</span>{whenStr} <span className="text-neutral-500">({bookedTz.replace(/_/g, " ")})</span></div>
            <div><span className="overline mr-2">Where</span>{booked.location}</div>
          </div>
          <div className="mt-6 pt-6 border-t border-neutral-200">
            <div className="overline mb-3">Add to your calendar</div>
            <div className="flex flex-wrap gap-2">
              <a href={booked.calendar_links.google} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 border border-neutral-300 rounded text-sm hover:bg-neutral-900 hover:text-white transition-colors"><GoogleLogo size={16} /> Google Calendar</a>
              <a href={booked.calendar_links.outlook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 border border-neutral-300 rounded text-sm hover:bg-neutral-900 hover:text-white transition-colors"><MicrosoftOutlookLogo size={16} /> Outlook</a>
              <a href={booked.calendar_links.office365} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 border border-neutral-300 rounded text-sm hover:bg-neutral-900 hover:text-white transition-colors">Office 365</a>
              <a href={ics} className="inline-flex items-center gap-2 px-3 py-2 border border-neutral-300 rounded text-sm hover:bg-neutral-900 hover:text-white transition-colors"><AppleLogo size={16} /> Apple / .ics</a>
            </div>
          </div>
          <p className="mt-6 text-xs text-neutral-500">A confirmation has been sent to {form.prospect_email}.</p>
        </Card>
        <Toaster />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="public-booking">
      <div className="max-w-5xl mx-auto py-12 px-6">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-9 h-9 bg-neutral-900 grid place-items-center rounded">
            <Compass size={20} weight="fill" color="white" />
          </div>
          <div>
            <div className="font-display text-2xl leading-none">{info?.business_name || "Prospexus"}</div>
            <div className="overline mt-1">Book a discovery call</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <h1 className="font-display text-5xl tracking-tighter leading-none">Let's talk.</h1>
            <p className="mt-4 text-neutral-600 text-lg leading-snug">Grab a 30-minute slot with {info?.sender_name || "our team"}. We'll walk you through how we can help.</p>
            <div className="mt-8 space-y-3 text-sm">
              <div className="flex items-center gap-3 text-neutral-600"><Clock size={16} /> 30 minute meeting</div>
              <div className="flex items-center gap-3 text-neutral-600"><MapPin size={16} /> Video call — link sent after booking</div>
              <div className="flex items-center gap-3 text-neutral-600"><Globe size={16} /> Times shown in <strong className="text-neutral-900 ml-1">{tz.replace(/_/g, " ")}</strong></div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <Card className="p-8 rounded-md border-neutral-200 bg-white">
              <div className="overline mb-3">1 · Pick a date</div>
              <CalendarView mode="single" selected={date} onSelect={(d) => d && setDate(d)} className="rounded-md" data-testid="public-calendar" />

              <div className="flex items-end justify-between mt-6 mb-3 gap-4">
                <div className="overline">2 · Choose a time</div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-neutral-500">Your time zone</Label>
                  <Select value={tz} onValueChange={setTz}>
                    <SelectTrigger className="w-56 h-8 text-xs" data-testid="tz-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {timezoneOptions.map(t => (
                        <SelectItem key={t} value={t}>{tzLabel(t)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {slots.length === 0 && (
                  <div className="col-span-4 py-6 text-center text-sm text-neutral-500" data-testid="no-slots">
                    No slots available on this day. Try another date.
                  </div>
                )}
                {slots.map(t => (
                  <button
                    key={t.iso}
                    onClick={() => setSlot(t)}
                    data-testid={`slot-${t.label}`}
                    className={`px-3 py-2 text-sm rounded border transition-colors ${
                      slot?.iso === t.iso
                        ? "bg-neutral-900 text-white border-neutral-900"
                        : "border-neutral-300 hover:border-neutral-900"
                    }`}
                  >{t.label}</button>
                ))}
              </div>

              <div className="overline mt-6 mb-3">3 · Your details</div>
              <div className="space-y-3">
                <div><Label>Your name</Label><Input value={form.prospect_name} onChange={e => setForm({...form, prospect_name: e.target.value})} data-testid="public-name" /></div>
                <div><Label>Email</Label><Input type="email" value={form.prospect_email} onChange={e => setForm({...form, prospect_email: e.target.value})} data-testid="public-email" /></div>
                <div><Label>What would you like to discuss?</Label><Textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="public-notes" /></div>
              </div>

              <Button className="mt-6 w-full bg-neutral-900 hover:bg-black text-white" onClick={submit} data-testid="public-book-btn">
                Confirm booking
              </Button>
            </Card>
          </div>
        </div>
      </div>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}
